import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Banknote,
  CheckCircle2,
  Loader2,
  Minus,
  Package,
  Plus,
  Receipt,
  Search,
  Smartphone,
  Tag,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateStorePosOrderMutation,
  useLookupPosMemberMutation,
  useStoreCatalog,
  useStoreId,
} from "@/hooks/queries";
import { printThermalReceipt } from "@/lib/print-thermal-receipt";
import { getApiErrorMessage } from "@/services/httpClient";
import { couponsService } from "@/services/couponsService";
import { PaymentMethod } from "@/types/enums";
import type { StorePosMemberLookupDto } from "@/types/order.dto";
import type { StoreProductRow } from "@/types/store.ui";

function digitsOnly(value: string) {
  return value.replace(/\D/g, "");
}

function memberQueryMatches(member: StorePosMemberLookupDto, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  if (member.memberCode?.toLowerCase() === q) return true;
  if (member.email?.toLowerCase() === q) return true;
  const qDigits = digitsOnly(query);
  const phoneDigits = digitsOnly(member.phone ?? "");
  if (qDigits.length >= 10 && phoneDigits.endsWith(qDigits.slice(-10))) return true;
  if (qDigits.length > 0 && phoneDigits === qDigits) return true;
  return false;
}

interface POSItem {
  productId: string;
  name: string;
  price: number;
  qty: number;
  image: string;
  businessVolume: number;
  stock: number | null;
}

const StorePos = () => {
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data: productsData, isLoading: productsLoading } = useStoreCatalog(storeId, 1, 200);
  const lookupMutation = useLookupPosMemberMutation(storeId ?? 0);
  const checkoutMutation = useCreateStorePosOrderMutation(storeId ?? 0);
  const { toast } = useToast();

  const storeProducts = useMemo(
    () =>
      (productsData?.items ?? []).filter(
        (p) => p.status === "active" || p.status === "out_of_stock",
      ),
    [productsData?.items],
  );

  const categories = useMemo(() => {
    const set = new Set<string>();
    storeProducts.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    return ["All", ...Array.from(set).sort()];
  }, [storeProducts]);

  const [posItems, setPosItems] = useState<POSItem[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [posSearch, setPosSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [memberQuery, setMemberQuery] = useState("");
  const [member, setMember] = useState<StorePosMemberLookupDto | null>(null);
  const [memberLookupError, setMemberLookupError] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number } | null>(null);
  const [posPayment, setPosPayment] = useState<"cash" | "upi">("cash");
  const lookupRequestId = useRef(0);

  const posFilteredProducts = storeProducts.filter((p) => {
    const matchesSearch = !posSearch || p.name.toLowerCase().includes(posSearch.toLowerCase());
    const matchesCategory = category === "All" || p.category === category;
    return matchesSearch && matchesCategory;
  });

  const itemCount = posItems.reduce((sum, i) => sum + i.qty, 0);
  const subTotal = posItems.reduce((sum, i) => sum + i.price * i.qty, 0);
  const discount = Math.min(appliedCoupon?.discount ?? 0, subTotal);
  const posTotal = Math.max(0, subTotal - discount);
  const cartBv = Number(
    posItems.reduce((sum, i) => sum + i.businessVolume * i.qty, 0).toFixed(2),
  );

  const addToPOS = (product: StoreProductRow) => {
    if (product.status === "out_of_stock" || (product.stock != null && product.stock <= 0)) {
      toast({ title: "Out of stock", description: product.name, variant: "destructive" });
      return;
    }
    setPosItems((prev) => {
      const existing = prev.find((i) => i.productId === product.id);
      if (existing) {
        if (product.stock != null && existing.qty >= product.stock) {
          toast({ title: "Stock limit reached", description: product.name, variant: "destructive" });
          return prev;
        }
        return prev.map((i) => (i.productId === product.id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [
        ...prev,
        {
          productId: product.id,
          name: product.name,
          price: product.price,
          qty: 1,
          image: product.image,
          businessVolume: product.businessVolume ?? 0,
          stock: product.stock,
        },
      ];
    });
    setSelectedLineId(product.id);
  };

  const updatePOSQty = (productId: string, delta: number) => {
    setPosItems((prev) =>
      prev
        .map((i) => {
          if (i.productId !== productId) return i;
          const next = i.qty + delta;
          const max = i.stock ?? 9999;
          return { ...i, qty: Math.min(Math.max(next, 0), max) };
        })
        .filter((i) => i.qty > 0),
    );
  };

  const removeLine = (productId: string) => {
    setPosItems((prev) => prev.filter((i) => i.productId !== productId));
    if (selectedLineId === productId) setSelectedLineId(null);
  };

  const clearBill = () => {
    setPosItems([]);
    setSelectedLineId(null);
    setCouponCode("");
    setAppliedCoupon(null);
  };

  const runMemberLookup = async (query: string, opts?: { silent?: boolean }) => {
    if (!storeId) return;
    const code = query.trim();
    if (!code) {
      setMember(null);
      setMemberLookupError(null);
      if (!opts?.silent) toast({ title: "Enter member ID, mobile, or username", variant: "destructive" });
      return;
    }

    const requestId = ++lookupRequestId.current;
    try {
      const result = await lookupMutation.mutateAsync(code);
      if (requestId !== lookupRequestId.current) return;
      setMember(result);
      setMemberQuery(result.memberCode);
      setMemberLookupError(null);
      if (!opts?.silent) {
        toast({ title: "Member verified", description: result.fullName });
      }
    } catch (error) {
      if (requestId !== lookupRequestId.current) return;
      setMember(null);
      const message = getApiErrorMessage(error);
      setMemberLookupError(message);
      if (!opts?.silent) {
        toast({ title: "Member not found", description: message, variant: "destructive" });
      }
    }
  };

  const handleLookupMember = async () => {
    await runMemberLookup(memberQuery, { silent: false });
  };

  // Auto-verify as the cashier types member ID / mobile / username.
  useEffect(() => {
    if (!storeId) return;
    const q = memberQuery.trim();

    if (!q) {
      setMember(null);
      setMemberLookupError(null);
      return;
    }

    if (member && memberQueryMatches(member, q)) {
      setMemberLookupError(null);
      return;
    }

    // Keep Charge disabled until a fresh lookup succeeds for the new query.
    if (member && !memberQueryMatches(member, q)) {
      setMember(null);
    }

    const digits = digitsOnly(q);
    const looksLikePhone = digits.length >= 10;
    const ready = looksLikePhone || q.length >= 3;
    if (!ready) {
      setMemberLookupError(null);
      return;
    }

    const timer = window.setTimeout(() => {
      void runMemberLookup(q, { silent: true });
    }, 450);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce on query only
  }, [memberQuery, storeId]);

  const handleApplyCoupon = async () => {
    if (!storeId) return;
    const code = couponCode.trim();
    if (!code) {
      toast({ title: "Enter coupon code", variant: "destructive" });
      return;
    }
    if (posItems.length === 0) {
      toast({ title: "Add products first", variant: "destructive" });
      return;
    }
    try {
      const result = await couponsService.validate({ code, orderAmount: subTotal, storeId });
      if (!result.isValid) {
        setAppliedCoupon(null);
        toast({
          title: "Coupon not applied",
          description: result.message || "Invalid coupon",
          variant: "destructive",
        });
        return;
      }
      setAppliedCoupon({ code: code.toUpperCase(), discount: result.discountAmount });
      setCouponCode(code.toUpperCase());
      toast({ title: "Coupon applied", description: `−₹${result.discountAmount}` });
    } catch (error) {
      setAppliedCoupon(null);
      toast({ title: "Coupon failed", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleCheckout = async () => {
    if (!storeId) return;
    if (!member) {
      toast({ title: "Verify Customer Care ID first", variant: "destructive" });
      return;
    }
    if (posItems.length === 0) {
      toast({ title: "Cart is empty", variant: "destructive" });
      return;
    }

    try {
      const order = await checkoutMutation.mutateAsync({
        memberCode: member.memberCode,
        couponCode: appliedCoupon?.code || couponCode.trim() || undefined,
        paymentMethod: posPayment === "cash" ? PaymentMethod.COD : PaymentMethod.Online,
        items: posItems.map((i) => ({
          productId: Number(i.productId),
          quantity: i.qty,
        })),
      });
      toast({
        title: "Sale completed",
        description: `${order.orderNumber} · ${member.fullName} · ₹${order.totalAmount}`,
      });
      printThermalReceipt(order, { paperWidthMm: 80 });
      clearBill();
      setMember(null);
      setMemberQuery("");
      setMemberLookupError(null);
    } catch (error) {
      toast({
        title: "Billing failed",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  if (storeLoading || productsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-14 w-full rounded-xl" />
        <Skeleton className="h-[70vh] rounded-xl" />
      </div>
    );
  }

  if (!storeId) {
    return (
      <div className="rounded-xl border bg-card p-8 text-center text-muted-foreground">
        No store linked to your account.
      </div>
    );
  }

  return (
    <div className="pos-terminal -mx-3 -mb-3 flex flex-col gap-3 px-3 pb-8 sm:-mx-4 sm:px-4 sm:pb-6 lg:-mx-6 lg:mb-0 lg:h-[calc(100dvh-7.5rem)] lg:min-h-[640px] lg:px-6 lg:pb-4">
      {/* Terminal top bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border/80 bg-[hsl(220_18%_12%)] px-3 py-3 text-white shadow-sm sm:gap-3 sm:px-4">
        <div className="min-w-0">
          <p className="font-heading text-base font-bold tracking-tight sm:text-lg">POS Terminal</p>
          <p className="text-[11px] text-white/60 sm:text-xs">Counter billing · Member optional coupon</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <Badge className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20">
            {storeProducts.filter((p) => p.status === "active").length} SKUs
          </Badge>
          <Badge variant="outline" className="border-white/20 text-white/80">
            {itemCount} in cart
          </Badge>
          <Button
            size="sm"
            variant="secondary"
            className="bg-white/15 text-white hover:bg-white/25 lg:hidden"
            onClick={() => {
              document.getElementById("pos-bill-rail")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <Receipt className="mr-1 h-3.5 w-3.5 text-[hsl(28_90%_55%)]" />
            Bill ({itemCount})
          </Button>
          <Button size="sm" variant="secondary" className="bg-white/10 text-white hover:bg-white/20" asChild>
            <Link to="/store/orders">Orders</Link>
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-3.5 lg:grid-cols-[minmax(0,1.4fr)_minmax(340px,0.95fr)] xl:grid-cols-[minmax(0,1.5fr)_minmax(380px,1fr)]">
        {/* Catalog */}
        <section className="flex max-h-[55vh] min-h-[280px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm lg:max-h-none lg:min-h-0">
          <div className="space-y-3 border-b border-border/70 p-3 sm:p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={posSearch}
                onChange={(e) => setPosSearch(e.target.value)}
                placeholder="Search products..."
                className="h-11 rounded-xl border-border/80 bg-background pl-10 text-base"
                autoFocus
              />
            </div>
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-2 pb-1">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={cn(
                      "shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors",
                      category === cat
                        ? "bg-[hsl(28_90%_48%)] text-white shadow-sm"
                        : "bg-secondary text-muted-foreground hover:bg-secondary/80",
                    )}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
            {posFilteredProducts.length === 0 ? (
              <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                <Package className="h-12 w-12 opacity-30" />
                <p className="font-medium text-foreground">No products in this view</p>
                <p className="max-w-sm text-sm">
                  Only approved &amp; active catalog items appear here. Approve products from admin, then refresh.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                {posFilteredProducts.map((product) => {
                  const inCart = posItems.find((i) => i.productId === product.id);
                  const unavailable =
                    product.status === "out_of_stock" || (product.stock != null && product.stock <= 0);
                  return (
                    <motion.button
                      key={product.id}
                      type="button"
                      whileTap={unavailable ? undefined : { scale: 0.97 }}
                      disabled={unavailable}
                      onClick={() => addToPOS(product)}
                      className={cn(
                        "group relative flex flex-col overflow-hidden rounded-xl border bg-background text-left transition-all",
                        unavailable
                          ? "cursor-not-allowed border-border/50 bg-muted/40 opacity-60"
                          : inCart
                            ? "border-[hsl(28_90%_48%)] shadow-sm ring-1 ring-[hsl(28_90%_48%)]/25"
                            : "border-border/70 hover:border-[hsl(28_90%_48%)]/55 hover:shadow-sm",
                      )}
                    >
                      <div className="relative aspect-square bg-muted/40">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <Package className="h-7 w-7 text-muted-foreground/35" />
                          </div>
                        )}
                        {inCart ? (
                          <span className="absolute right-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-[hsl(28_90%_48%)] px-1 text-[11px] font-bold text-white shadow">
                            {inCart.qty}
                          </span>
                        ) : null}
                        {unavailable ? (
                          <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[9px] font-semibold uppercase tracking-wide text-white">
                            Out of stock
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-1 flex-col gap-0.5 px-2 pb-2 pt-1.5">
                        <p className="line-clamp-2 text-[12px] font-semibold leading-tight text-foreground sm:line-clamp-1">
                          {product.name}
                        </p>
                        <p className="truncate text-[10px] leading-tight text-muted-foreground">
                          {product.category}
                        </p>
                        <div className="mt-auto flex items-end justify-between gap-1 pt-1">
                          <div className="min-w-0">
                            <p className="text-[13px] font-bold tabular-nums leading-none text-foreground">
                              ₹{product.price}
                            </p>
                            {product.originalPrice != null && product.originalPrice > product.price ? (
                              <p className="mt-0.5 text-[10px] tabular-nums text-muted-foreground line-through">
                                ₹{product.originalPrice}
                              </p>
                            ) : null}
                          </div>
                          {(product.businessVolume ?? 0) > 0 ? (
                            <span className="shrink-0 text-[10px] font-bold tabular-nums text-teal-600 dark:text-teal-400">
                              {Number(product.businessVolume).toFixed(
                                Number(product.businessVolume) % 1 === 0 ? 0 : 2,
                              )}{" "}
                              BV
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Bill rail */}
        <aside
          id="pos-bill-rail"
          className="flex flex-col overflow-y-auto overflow-x-hidden rounded-2xl border border-border bg-[hsl(40_20%_98%)] shadow-sm dark:bg-[hsl(220_14%_10%)] lg:min-h-0"
        >
          <div className="sticky top-0 z-20 shrink-0 border-b border-border/70 bg-[hsl(220_18%_12%)] px-4 py-3 text-white">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Receipt className="h-5 w-5 shrink-0 text-[hsl(28_90%_55%)]" />
                <div className="min-w-0">
                  <p className="text-sm font-bold">Current Bill</p>
                  <p className="text-[11px] text-white/55">
                    {itemCount} items · ₹{posTotal.toLocaleString()}
                  </p>
                </div>
              </div>
              {posItems.length > 0 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 shrink-0 text-white/80 hover:bg-white/10 hover:text-white"
                  onClick={clearBill}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" /> Clear
                </Button>
              ) : null}
            </div>
          </div>

          <div className="shrink-0 space-y-3 border-b border-border/60 bg-[hsl(40_20%_98%)] dark:bg-[hsl(220_14%_10%)] p-3">
            <label className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Customer (auto-verify)
            </label>
            <div className="flex gap-2">
              <Input
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleLookupMember();
                  }
                }}
                placeholder="Member ID / mobile"
                className={cn(
                  "h-10 min-w-0 flex-1 rounded-xl bg-background font-mono text-sm",
                  member && "border-emerald-500/60 focus-visible:ring-emerald-500/30",
                )}
              />
              <Button
                type="button"
                className="h-10 shrink-0 rounded-xl bg-[hsl(28_90%_48%)] hover:bg-[hsl(28_90%_42%)]"
                onClick={() => void handleLookupMember()}
                disabled={lookupMutation.isPending}
              >
                {lookupMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            {member ? (
              <div className="flex items-start gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{member.fullName}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {member.memberCode}
                    {member.phone ? ` · ${member.phone}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  className="ml-auto text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setMember(null);
                    setMemberQuery("");
                    setMemberLookupError(null);
                  }}
                  aria-label="Clear member"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <p
                className={cn(
                  "flex items-center gap-1.5 text-xs",
                  memberLookupError ? "text-destructive" : "text-muted-foreground",
                )}
              >
                {lookupMutation.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying customer…
                  </>
                ) : memberLookupError ? (
                  <>
                    <UserRound className="h-3.5 w-3.5" /> {memberLookupError}
                  </>
                ) : (
                  <>
                    <UserRound className="h-3.5 w-3.5" /> Type member ID, mobile, or username — auto-verifies
                  </>
                )}
              </p>
            )}
          </div>

          <div className="flex-1 min-h-[220px] sm:min-h-[260px] lg:min-h-[240px] max-h-[50vh] lg:max-h-none overflow-y-auto px-2.5 py-2.5">
            <AnimatePresence initial={false}>
              {posItems.length === 0 ? (
                <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 px-4 text-center text-muted-foreground lg:min-h-[240px]">
                  <Receipt className="h-10 w-10 opacity-30 text-[hsl(28_90%_55%)]" />
                  <p className="text-sm font-semibold text-foreground/80">Tap products to build the bill</p>
                  <p className="text-xs text-muted-foreground">Use + / − on each line to change quantity</p>
                </div>
              ) : (
                posItems.map((item) => {
                  const selected = selectedLineId === item.productId;
                  const lineBv = Number((item.businessVolume * item.qty).toFixed(2));
                  return (
                    <motion.button
                      key={item.productId}
                      type="button"
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      onClick={() => setSelectedLineId(item.productId)}
                      className={cn(
                        "mb-1.5 flex w-full flex-col gap-2 rounded-xl border px-2.5 py-2.5 text-left transition-colors sm:flex-row sm:items-center sm:gap-2 sm:px-2 sm:py-2",
                        selected
                          ? "border-[hsl(28_90%_48%)] bg-[hsl(28_90%_48%)]/10"
                          : "border-transparent bg-background/80 hover:bg-background",
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt=""
                            className="h-11 w-11 shrink-0 rounded-lg bg-muted object-cover"
                          />
                        ) : (
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground/40">
                            <Package className="h-5 w-5" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold">{item.name}</p>
                          <p className="text-[11px] tabular-nums text-muted-foreground">
                            ₹{item.price} × {item.qty}
                            {item.businessVolume > 0 ? ` · ${lineBv} BV` : ""}
                          </p>
                        </div>
                        <div className="shrink-0 text-right sm:hidden">
                          <p className="text-sm font-bold tabular-nums">₹{item.price * item.qty}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 sm:contents">
                        <div className="flex items-center gap-1">
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              updatePOSQty(item.productId, -1);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                updatePOSQty(item.productId, -1);
                              }
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-md border bg-card hover:bg-muted sm:h-7 sm:w-7"
                          >
                            <Minus className="h-3 w-3" />
                          </span>
                          <span className="w-7 text-center text-sm font-bold tabular-nums">{item.qty}</span>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              updatePOSQty(item.productId, 1);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                updatePOSQty(item.productId, 1);
                              }
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-md border bg-card hover:bg-muted sm:h-7 sm:w-7"
                          >
                            <Plus className="h-3 w-3" />
                          </span>
                        </div>
                        <div className="hidden w-16 text-right sm:block">
                          <p className="text-sm font-bold tabular-nums">₹{item.price * item.qty}</p>
                          <button
                            type="button"
                            className="text-[10px] text-destructive hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeLine(item.productId);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                        <button
                          type="button"
                          className="text-xs text-destructive hover:underline sm:hidden"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLine(item.productId);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </motion.button>
                  );
                })
              )}
            </AnimatePresence>
          </div>

          <div className="mt-auto shrink-0 space-y-3 border-t border-border/70 bg-card/95 backdrop-blur-md p-3.5 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1">
                <Tag className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value);
                    setAppliedCoupon(null);
                  }}
                  placeholder="Coupon"
                  className="h-10 rounded-lg bg-background pl-8 text-sm uppercase"
                />
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-10 shrink-0 rounded-lg px-4"
                onClick={() => void handleApplyCoupon()}
              >
                Apply
              </Button>
            </div>

            <div className="space-y-1 rounded-xl border border-dashed border-border/80 bg-card px-3 py-2.5 font-mono text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span className="tabular-nums">₹{subTotal.toLocaleString()}</span>
              </div>
              {discount > 0 ? (
                <div className="flex justify-between text-emerald-600">
                  <span>Coupon ({appliedCoupon?.code})</span>
                  <span className="tabular-nums">−₹{discount.toLocaleString()}</span>
                </div>
              ) : null}
              {cartBv > 0 ? (
                <div className="flex justify-between text-muted-foreground">
                  <span>BV credit</span>
                  <span className="tabular-nums">
                    {Number.isInteger(cartBv) ? cartBv : cartBv.toFixed(2)}
                  </span>
                </div>
              ) : null}
              <div className="flex items-end justify-between gap-3 border-t border-border/60 pt-2">
                <span className="font-heading text-base font-bold">Payable</span>
                <span className="font-heading text-xl font-bold tabular-nums text-[hsl(28_90%_42%)] sm:text-2xl">
                  ₹{posTotal.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPosPayment("cash")}
                className={cn(
                  "flex h-11 items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-colors sm:h-12",
                  posPayment === "cash"
                    ? "border-[hsl(28_90%_48%)] bg-[hsl(28_90%_48%)] text-white"
                    : "border-border bg-card text-muted-foreground hover:bg-muted",
                )}
              >
                <Banknote className="h-4 w-4" /> Cash
              </button>
              <button
                type="button"
                onClick={() => setPosPayment("upi")}
                className={cn(
                  "flex h-11 items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-colors sm:h-12",
                  posPayment === "upi"
                    ? "border-[hsl(28_90%_48%)] bg-[hsl(28_90%_48%)] text-white"
                    : "border-border bg-card text-muted-foreground hover:bg-muted",
                )}
              >
                <Smartphone className="h-4 w-4" /> UPI
              </button>
            </div>

            <Button
              className="h-12 w-full rounded-xl bg-[hsl(28_90%_42%)] text-base font-bold shadow-md hover:bg-[hsl(28_90%_36%)] sm:h-14"
              disabled={!member || posItems.length === 0 || checkoutMutation.isPending}
              onClick={() => void handleCheckout()}
            >
              {checkoutMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing…
                </>
              ) : (
                <>
                  <Receipt className="mr-2 h-5 w-5" />
                  Charge ₹{posTotal.toLocaleString()}
                </>
              )}
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default StorePos;
